import React, { useState, useCallback, type ChangeEvent } from 'react';
import { Checkbox } from '@atlaskit/checkbox';
import { Label } from '@atlaskit/form';
import type { TerminalClause, CompoundClause } from '@atlaskit/jql-ast';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { FixedWidthPopupComponent } from '@atlassian/jira-jql-builder-basic-picker/src/ui/popup-container/index.tsx';
import removeFieldClauseFromJql from '@atlassian/jira-jql-utils/src/utils/remove-field-clause.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { OnSearchWrapper } from '../../../../common/types.tsx';
import { labelContainerTestId } from '../../../../common/ui/picker-trigger/constants.tsx';
import PickerTrigger from '../../../../common/ui/picker-trigger/main.tsx';
import { useAST } from '../../../../controllers/ast/index.tsx';
import {
	useShouldRenderToParent,
	useVerticalMode,
} from '../../../../controllers/feature-context.tsx';
import { PickerTriggerLayout } from '../../../../common/ui/picker-trigger-layout/index.tsx';
import messages from './messages.tsx';
import { getUpdatedJql, getInitialState } from './utils/get-updated-jql.tsx';

type Props = {
	activePopupKey: string;
	clause: TerminalClause | CompoundClause | undefined;
	fieldDescription: string | null;
	fieldId: string;
	fieldLabel: string | undefined;
	onClear: (onClearJqlClause: string | undefined) => void;
	onClose: () => void;
	onOpen: () => void;
	onSearchWrapper?: OnSearchWrapper;
	isIrremovable?: boolean;
};

const BooleanValueContent = ({
	fieldLabel,
	isChecked,
	clause,
}: {
	fieldLabel: string;
	isChecked: boolean;
	clause: TerminalClause | CompoundClause | undefined;
}) => {
	const { formatMessage } = useIntl();
	const verticalMode = useVerticalMode();

	if (!fg('list_lovability_improving_filters')) {
		return null;
	}

	const valueMessage = isChecked
		? formatMessage(messages.trueMessage)
		: formatMessage(messages.all);

	if (verticalMode) {
		return (
			<PickerTriggerLayout label={fieldLabel}>{clause ? valueMessage : null}</PickerTriggerLayout>
		);
	}

	return (
		<Text color="inherit" weight="medium" testId={labelContainerTestId}>
			{clause ? (
				<>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Text as="strong">{`${fieldLabel}: `}</Text>
					{valueMessage}
				</>
			) : (
				fieldLabel
			)}
		</Text>
	);
};

export const LastUserCommentedPickerWrapper = ({
	activePopupKey,
	clause,
	fieldId,
	fieldLabel,
	fieldDescription,
	onClear,
	onClose,
	onOpen,
	onSearchWrapper,
	isIrremovable,
}: Props): React.JSX.Element => {
	const [isChecked, setIsChecked] = useState(getInitialState(clause));
	const isOpen = activePopupKey === fieldId;
	const handleOnTriggerClick = useCallback(
		() => (isOpen ? onClose() : onOpen()),
		[isOpen, onClose, onOpen],
	);
	const [ast] = useAST();
	const { formatMessage } = useIntl();
	const shouldRenderToParent = useShouldRenderToParent();

	return (
		<Popup
			key={fieldId}
			placement="bottom-start"
			isOpen={isOpen}
			onClose={onClose}
			popupComponent={FixedWidthPopupComponent}
			shouldRenderToParent={shouldRenderToParent}
			messageId="jql-builder-basic.ui.jql-editor.field.last-user-commented-picker-wrapper.popup"
			messageType="transactional"
			trigger={(triggerProps: TriggerProps) => (
				<PickerTrigger
					{...triggerProps}
					isInvalid={false}
					isSelected={isOpen}
					hasValues={!!clause}
					onClick={handleOnTriggerClick}
					onClear={() => onClear(clause ? removeFieldClauseFromJql(ast, clause) : undefined)}
					testId={`jql-builder-basic.ui.jql-editor.picker.filter-button.${fieldId}`}
					fieldDescription={fieldDescription}
					isIrremovable={isIrremovable}
				>
					{fg('list_lovability_improving_filters') ? (
						<BooleanValueContent
							fieldLabel={fieldLabel || ''}
							isChecked={isChecked}
							clause={clause}
						/>
					) : (
						<Text color="inherit" weight="medium" testId={labelContainerTestId}>
							{clause ? (
								<>
									{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
									<Text as="strong">{`${fieldLabel}: `}</Text>
									{isChecked ? formatMessage(messages.trueMessage) : formatMessage(messages.all)}
								</>
							) : (
								fieldLabel
							)}
						</Text>
					)}
				</PickerTrigger>
			)}
			content={() => (
				<Box xcss={boxStyles}>
					<Checkbox
						id="last-user-commented-checkbox"
						onChange={(e: ChangeEvent<HTMLInputElement>): void => {
							setIsChecked(e.target.checked);
							onSearchWrapper &&
								onSearchWrapper(getUpdatedJql(ast, clause, fieldId, e.target.checked));
							onClose();
						}}
						isChecked={isChecked}
						testId="jql-builder-basic.ui.jql-editor.field.last-user-commented-picker-wrapper.checkbox"
					/>
					<Label htmlFor="last-user-commented-checkbox">
						{formatMessage(messages.checkBoxLabel)}
					</Label>
				</Box>
			)}
		/>
	);
};

export default LastUserCommentedPickerWrapper;

const boxStyles = xcss({
	padding: 'space.150',
});
